import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { connect } from 'react-redux';
import { Button, ButtonContainer, Typography } from '@emburse/ui-core';
import { BulkUploadWizardError } from '@ReactComponents/BulkUploadWizardError/BulkUploadWizardError';
import { BulkUploadWizardErrorDetails } from '@ReactComponents/BulkUploadWizardErrorDetails';
// Components
import { Dropzone } from '@ReactComponents/Dropzone';
import { Spinner } from '@ReactComponents/Spinner';
import { WizardPanel } from '@ReactComponents/Wizard/Wizard';
import { ClearProcessingFailure, ReceiveBulkUploadDiffs, ReceiveCardStatementUploadDiffs, RecordProcessingFailure, SaveCsvData, ToggleViewingErrorDetails } from '@Actions/bulk_upload';
import { BulkUploadApiService as bulkUploadApiService } from '@ClientApi/bulkUpload';
import { formatDate } from '@Shared/helpers/abacusTimeFormatter';
function renderProgramIdentifierWithPriorUpload(account) {
    const lastUpdateString = account.last_successful_update_at && formatDate(account.last_successful_update_at, 'PPP pp');
    return (_jsxs("div", Object.assign({ className: "card-statement-program-info" }, { children: [_jsx("strong", { children: account.name }), lastUpdateString && _jsxs("span", { children: [" Last Upload ", lastUpdateString] }), _jsx("hr", {})] })));
}
function renderEmbarkLoadingPanel() {
    return (_jsx("div", Object.assign({ style: { minHeight: '100px' } }, { children: _jsx(Spinner, { small: true, centered: true, loadingText: "Processing File" }) })));
}
export class BulkUploadWizardStart extends WizardPanel {
    constructor(props) {
        super(props);
        this.state = {
            isDragging: false
        };
    }
    getInstructionsText() {
        const { uploadType, bucket } = this.props;
        if (uploadType === 'team') {
            return `Update your team's ${window.whitelabelConfiguration.tags.brandName} account with the most recent record of your employees' information.`;
        }
        else if (uploadType === 'categories') {
            return `Update your team's ${window.whitelabelConfiguration.tags.brandName} account with your current set of categories.`;
        }
        else if (uploadType === 'card_statement') {
            return (_jsxs(_Fragment, { children: ["Upload a statement from your card provider.", window.whitelabelConfiguration.tags.brandName, " will identify new card holders from your file, as well as any potential duplicates."] }));
        }
        else if (uploadType === 'expense_tags') {
            const bucketType = bucket.bucket_type === 'expense_field' ? 'Expense Field' : 'Team Field';
            return `Update ${bucketType} "${bucket.name}" with your current set of items.`;
        }
    }
    onDrop(acceptedFiles) {
        var _a;
        const me = this;
        const uploadType = this.props.uploadType;
        const account = this.props.cardProgram;
        const bucketId = (_a = this.props.bucket) === null || _a === void 0 ? void 0 : _a.id;
        this.setState({ isDragging: false, isUploading: true });
        if (acceptedFiles.length) {
            const file = acceptedFiles[0];
            const r = new FileReader();
            r.onloadend = function (e) {
                const data = e.target.result;
                const fileNameSplitOnPeriods = file.name.split('.');
                const fileType = fileNameSplitOnPeriods[fileNameSplitOnPeriods.length - 1].toLowerCase();
                const accountId = (account && account.id) || null;
                bulkUploadApiService.uploadFile(uploadType, data, fileType, accountId, bucketId).then((responseObj) => {
                    if (!responseObj.valid) {
                        me.props.recordProcessingFailure(responseObj.message, responseObj.diffs);
                        me.setState({ isUploading: false });
                    }
                    else {
                        me.props.onSaveCsvData(data, fileType);
                        me.props.receiveDiffs(responseObj.diffs);
                        me.props.changePanel('summary');
                    }
                });
            };
            r.readAsText(file);
        }
    }
    onDragLeave() {
        this.toggleDrag(false);
    }
    onDragEnter() {
        this.toggleDrag(true);
    }
    toggleDrag(isDragging = false) {
        if (this.state.isDragging === isDragging) {
            return;
        }
        this.setState({ isDragging });
    }
    onDownloadTemplate() {
        const { uploadType, categoryFilterBucket, tagBuckets, isManualSync } = this.props;
        if (uploadType === 'team') {
            let tagBucketNames = '';
            if (tagBuckets && tagBuckets.length) {
                tagBucketNames = ',' + tagBuckets.map((bucket) => bucket.name);
            }
            let csvContent = 'Email,Role,Manager Email';
            if (window.ORG.is_manager_card_issuing_enabled) {
                csvContent += ',Manager Card Issuing Limit, Is Physical Card Issuing Enabled';
            }
            if (window.FEATURES.show_employee_ids) {
                csvContent += ',Employee ID';
            }
            if (window.FEATURES.travel) {
                csvContent += ',Travel Enabled';
            }
            csvContent += tagBucketNames;
            const encodedUri = encodeURI(csvContent);
            return encodedUri;
        }
        if (uploadType === 'categories') {
            let csvContent = 'Category,Subcategory';
            // Add Visible to: xxxxx if a team field is scoped
            if (categoryFilterBucket) {
                csvContent += `,Visible to: ${categoryFilterBucket.name}`;
            }
            if (isManualSync) {
                csvContent += ',Chart of Accounts';
            }
            const encodedUri = encodeURI(csvContent);
            return encodedUri;
        }
        if (uploadType === 'card_statement') {
            const csvContent = 'Transaction Date,Posted Date,Card No.,Merchant Name,Category,Debit,Credit';
            const encodedUri = encodeURI(csvContent);
            return encodedUri;
        }
        if (uploadType === 'expense_tags') {
            const csvContent = ['Name', 'Description'].join(',');
            const encodedUri = encodeURI(csvContent);
            return encodedUri;
        }
    }
    renderEmbarkStartPanel(uploadType) {
        if (this.state.isUploading) {
            return renderEmbarkLoadingPanel();
        }
        const instructionsText = this.getInstructionsText();
        const teamBulkUploadDocumentationLink = (_jsx("a", Object.assign({ target: "_blank", rel: "noopener noreferrer", style: { marginRight: 30 }, href: window.whitelabelConfiguration.supportLinks.bulkUpdateTeam }, { children: "About format" })));
        const categoryBulkUploadDocumentationLink = (_jsx("a", Object.assign({ target: "_blank", rel: "noopener noreferrer", style: { marginRight: 30 }, href: window.whitelabelConfiguration.supportLinks.bulkUpdateCategories }, { children: "About format" })));
        const statementUploadDocumentationLink = (_jsx("a", Object.assign({ target: "_blank", rel: "noopener noreferrer", style: { marginRight: 30 }, href: window.whitelabelConfiguration.supportLinks.manualStatementUpload }, { children: "About format" })));
        const customBodyText = uploadType === 'card_statement' ? 'Drag a file here' : null;
        return (_jsxs("div", { children: [uploadType === 'card_statement' && renderProgramIdentifierWithPriorUpload(this.props.cardProgram), _jsx(Typography, { children: instructionsText }), uploadType !== 'card_statement' && (_jsxs(Typography, { children: ["Your upload must be a properly formatted ", _jsx("em", { children: ".csv" }), " file."] })), uploadType === 'card_statement' && (_jsxs(Typography, { children: ["Your upload must be a properly formatted ", _jsx("em", { children: ".csv" }), " or ", _jsx("em", { children: ".ofx" }), " file."] })), uploadType === 'team' && teamBulkUploadDocumentationLink, uploadType === 'categories' && categoryBulkUploadDocumentationLink, uploadType === 'card_statement' && statementUploadDocumentationLink, _jsx("a", Object.assign({ href: `data:text/csv,${this.onDownloadTemplate()}`, download: "Bulk Upload Template.csv" }, { children: "Download template" })), _jsx(Dropzone, { customBodyText: customBodyText, isDragging: this.state.isDragging, multiple: false, onDragEnter: () => this.onDragEnter(), onDragLeave: () => this.onDragLeave(), onDrop: (acceptedFiles) => this.onDrop(acceptedFiles) })] }));
    }
    renderEmbarkErrorSummary(errorType) {
        return (_jsxs("div", { children: [_jsx(BulkUploadWizardError, { errorDiffs: this.props.errorDiffs, errorType: errorType, errorMessage: this.props.error }), _jsxs(ButtonContainer, Object.assign({ alignButtons: "right", style: { marginTop: 20 } }, { children: [_jsx(Button, Object.assign({ color: "secondary", onClick: () => this.props.clearError() }, { children: "Back" })), errorType === 'INLINE' && _jsx(Button, Object.assign({ onClick: () => this.props.toggleViewingErrorDetails() }, { children: "Review Errors" }))] }))] }));
    }
    renderEmbarkErrorDetails() {
        return (_jsxs("div", { children: [_jsx(BulkUploadWizardErrorDetails, { uploadType: this.props.uploadType, errorDiffs: this.props.errorDiffs }), _jsx(ButtonContainer, Object.assign({ alignButtons: "right", style: { marginTop: 20 } }, { children: _jsx(Button, Object.assign({ onClick: () => this.props.toggleViewingErrorDetails() }, { children: "Back" })) }))] }));
    }
    renderEmbarkErrorContent(props) {
        const errorType = props.errorDiffs.length ? 'INLINE' : 'FILE';
        return props.viewingErrorDetails ? this.renderEmbarkErrorDetails() : this.renderEmbarkErrorSummary(errorType);
    }
    render() {
        return _jsx("div", { children: this.props.error ? this.renderEmbarkErrorContent(this.props) : this.renderEmbarkStartPanel(this.props.uploadType) });
    }
}
// Connected Component
const mapStateToProps = (state, parentProps) => {
    const s = state.bulkUpload;
    return Object.assign(Object.assign({}, parentProps), { error: s.error, errorDiffs: s.errorDiffs, viewingErrorDetails: s.viewingErrorDetails });
};
const mapDispatchToProps = (dispatch) => ({
    clearError: () => {
        dispatch(new ClearProcessingFailure());
    },
    receiveDiffs: (diffs) => {
        dispatch(new ReceiveBulkUploadDiffs(diffs));
    },
    recordProcessingFailure: (error, errorDiffs) => {
        dispatch(new RecordProcessingFailure(error, errorDiffs));
    },
    toggleViewingErrorDetails: () => {
        dispatch(new ToggleViewingErrorDetails());
    },
    onSaveCsvData: (data) => {
        dispatch(new SaveCsvData(data, 'csv'));
    }
});
// Card Statement Upload connections
const cardStatementStateToProps = (state, parentProps) => {
    const s = state.bulkUpload;
    const company = state.company;
    return Object.assign(Object.assign({}, parentProps), { error: s.error, errorDiffs: s.errorDiffs, viewingErrorDetails: s.viewingErrorDetails, isManualSync: company.accountingIntegration && company.accountingIntegration.is_manual });
};
const cardStatementDispatchToProps = (dispatch) => ({
    clearError: () => {
        dispatch(new ClearProcessingFailure());
    },
    receiveDiffs: (diffs) => {
        dispatch(new ReceiveCardStatementUploadDiffs(diffs));
    },
    recordProcessingFailure: (error, errorDiffs) => {
        dispatch(new RecordProcessingFailure(error, errorDiffs));
    },
    toggleViewingErrorDetails: () => {
        dispatch(new ToggleViewingErrorDetails());
    },
    onSaveCsvData: (data, fileType) => {
        dispatch(new SaveCsvData(data, fileType));
    }
});
export const ConnectedBulkUploadWizardStart = connect(mapStateToProps, mapDispatchToProps)(BulkUploadWizardStart);
export const CardStatementUploadWizardStart = connect(cardStatementStateToProps, cardStatementDispatchToProps)(BulkUploadWizardStart);
